import { Grid } from "@material-ui/core";
import EmptyPlaceholder from "components/EmptyPlaceholder/EmptyPlaceholder";
import ExportButton from "components/ExportButton/ExportButton";
import { dateFilterTypes, getDateFilterRanges, loadStatusReasonOwner, onTimePerformancePercentages, onTimePerformancePercentagesObj } from "features/loads/components/LoadsDashboard/loadsDashboardConsts";
import { loadStatuses } from "features/loads/enums";
import { useGetLoadsStatusDashboardQuery, useGetLoadStatusReasonsQuery } from "features/loads/loadsSlice";
import { TenantNameResolver } from "features/saasManagement/TenantNameResolver/TenantNameResolver";
import { IconDownloadFile, IconTruck } from "icons";
import { useMemo, useState } from "react";
import { Bar, BarChart, LabelList, Legend, ResponsiveContainer, XAxis, YAxis } from "recharts";
import FormatService from "services/formatService";
import { arrayToObject } from "utils/object-util";
import LoadsListDialog from "../LoadsListDialog";
import WidgetTitle from "../WidgetTitle/WidgetTitle";
import { onTimePerformanceStyle } from "./style";

const OnTimePerformanceWidget = ({ loading }) => {
    const classes = onTimePerformanceStyle();

    const [selectedBar, setSelectedBar] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);

    const fourWeeks = getDateFilterRanges()?.[dateFilterTypes.WEEK].slice(0, 4).reverse();

    let startDate = fourWeeks[0].startDate?.toDateString();
    let endDate = fourWeeks[3].endDate?.toDateString();

    let { data: loads, error: loadListError, isFetching: isLoadingLoads } = useGetLoadsStatusDashboardQuery({ startDate: startDate, endDate: endDate });
    loads = loads || [];

    let { data: statusReasons, error: statusReasonsError, isLoading: isLoadingStatusReasons } = useGetLoadStatusReasonsQuery();
    statusReasons = statusReasons || [];

    const statusReasonsLookup = arrayToObject(statusReasons);

    loads = useMemo(() => {
        return loads?.filter(load => load.latestStatus != loadStatuses.CANCELLED)?.map(l => {
            const finalDeliveryDate = FormatService.formatDateIgnoreTime(new Date(l.finalDeliveryDate));
            const actualDeliveryDate = l.actualDeliveryDate ? FormatService.formatDateIgnoreTime(new Date(l.actualDeliveryDate)) : null;
            const currentDate = FormatService.formatDateIgnoreTime(new Date());

            const isDelayedLoad = (actualDeliveryDate && finalDeliveryDate < actualDeliveryDate) ||
                (!actualDeliveryDate && finalDeliveryDate < currentDate);

            return {
                ...l,
                loadDate: finalDeliveryDate,
                isDelayedLoad: isDelayedLoad,
                latestDelayObject: l?.statuses
                    ?.filter(s => s.status === loadStatuses.EXCEPTION)
                    ?.sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate))?.[0]
            };
        });
    }, [loads, statusReasonsLookup]);

    let groupedData = fourWeeks.map((week) => {
        let weekLoads = loads.filter((load) => {
            return load.loadDate >= week.startDate && load.loadDate <= week.endDate;
        });

        let delayedLoads = weekLoads?.filter((load) => load.isDelayedLoad);

        let onTimeLoads = weekLoads?.filter((load) => !load.isDelayedLoad);
        let clientDelays = delayedLoads?.filter((load) =>
            load?.latestDelayObject?.status == loadStatuses.EXCEPTION &&
            statusReasonsLookup?.[load?.latestDelayObject?.reasonId]?.owner == loadStatusReasonOwner.CLIENT);
        let tenantDelays = delayedLoads?.filter((load) =>
            load?.latestDelayObject?.status == loadStatuses.EXCEPTION &&
            statusReasonsLookup?.[load?.latestDelayObject?.reasonId]?.owner == loadStatusReasonOwner.TENANT);
        let otherDelays = delayedLoads?.filter((load) =>
            load?.latestDelayObject?.status == loadStatuses.EXCEPTION &&
            statusReasonsLookup?.[load?.latestDelayObject?.reasonId]?.owner == loadStatusReasonOwner.OTHER);
        let unspecifiedDelays = delayedLoads?.filter((load) =>
            (load?.latestDelayObject?.status != loadStatuses.EXCEPTION) ||
            (load?.latestDelayObject?.status == loadStatuses.EXCEPTION &&
                !statusReasonsLookup?.[load?.latestDelayObject?.reasonId]?.owner));
        let totalLoads = weekLoads.length;
        let onTimePercentage = (onTimeLoads.length / totalLoads) * 100;
        let clientDelaysPercentage = (clientDelays.length / totalLoads) * 100;
        let tenantDelaysPercentage = (tenantDelays.length / totalLoads) * 100;
        let otherDelaysPercentage = (otherDelays.length / totalLoads) * 100;
        let unspecifiedDelaysPercentage = (unspecifiedDelays.length / totalLoads) * 100;

        return {
            week: week,
            weekName: week.weekNumber,
            weekNumber: `Week ${week.id}`,
            loads: weekLoads,
            total: 100,
            onTimeList: onTimeLoads,
            clientDelaysList: clientDelays,
            tenantDelaysList: tenantDelays,
            otherDelaysList: otherDelays,
            unspecifiedDelaysList: unspecifiedDelays,
            onTime: onTimePercentage?.toFixed(1),
            clientDelays: clientDelaysPercentage?.toFixed(1),
            tenantDelays: tenantDelaysPercentage?.toFixed(1),
            otherDelays: otherDelaysPercentage?.toFixed(1),
            unspecifiedDelays: unspecifiedDelaysPercentage?.toFixed(1),
        };
    }
    );

    const mapExportData = (a => {
        return {
            ['Week']: a.weekName,
            ['On Time']: `${a.onTime}%`,
            ['Client Delays']: `${a.clientDelays}%`,
            [<TenantNameResolver endText='Delays' />]: `${a.tenantDelays}%`,
            ['Other Delays']: `${a.otherDelays}%`,
            ['Unspecified Delays']: `${a.unspecifiedDelays}%`,
        };
    })

    const onClickBar = (data, percentage) => {
        let finalData = {
            ...data,
            loads: data?.[percentage.listName] ?? [],
            percentageObj: percentage
        }
        setSelectedBar(finalData);
        setOpenDialog(true);
    }

    const CustomTitle = () => {
        return <div className={classes.dialogTitleWrapper}>
            <div className={classes.dialogTitle} style={{ color: selectedBar?.percentageObj?.color }}>
                <IconTruck />
                {selectedBar?.percentageObj?.name} Loads ({selectedBar?.loads?.length})
            </div>
        </div>
    }


    const isLoading = isLoadingLoads || loading || isLoadingStatusReasons;

    return <Grid container className={classes.graphWrapper}>
        <Grid item>
            <WidgetTitle
                title='On Time Performance'
                loading={isLoading}
            />
        </Grid>
        <div style={{ position: 'absolute', top: '1.3em', right: '0.5em' }}>
            <ExportButton
                btnStyle='text'
                label=""
                icon={<IconDownloadFile />}
                fileLabel="Export"
                data={groupedData}
                mapExportData={mapExportData}
            />
        </div>
        {!!groupedData?.length ? <ResponsiveContainer width="100%" height="100%">
            <BarChart
                width={500}
                height={300}
                data={groupedData}
                margin={{ top: 40, right: 30, left: -10, bottom: 10 }}
            >
                <XAxis
                    type="category"
                    axisLine={false}
                    interval={0}
                    dataKey="weekName"
                    tickLine={false}
                    tick={{
                        fontSize: '0.8em',
                        fill: '#787777',
                    }}
                />
                < YAxis
                    type="number"
                    axisLine={false}
                    tickLine={false}
                    dataKey="total"
                    tick={{
                        fontSize: '0.75em',
                        fontWeight: 600,
                        fill: '#787777',
                    }}
                    tickFormatter={(value) => `${value}%`}
                />
                <Legend
                    iconType="circle"
                    layout="horizontal"
                    verticalAlign="bottom"
                    iconSize={8}
                    wrapperStyle={{ bottom: 0, marginLeft: '1rem', fontSize: 11 }}
                    formatter={(value) => onTimePerformancePercentagesObj[value]?.name}
                />
                {onTimePerformancePercentages.map((percentage, index) => (

                    <Bar
                        dataKey={percentage.id}
                        stackId="a"
                        fill={percentage.color}
                        className={classes.bar}
                        cursor="pointer"
                        onClick={(data) => onClickBar(data, percentage)}>
                        <LabelList
                            dataKey={percentage.id}
                            position="inside"
                            className={classes.labelList}
                            formatter={(value) => value > 0 ? `${value}%` : ''}

                        />
                    </Bar>))
                }
            </BarChart>
        </ResponsiveContainer> :
            <EmptyPlaceholder text='No Loads' />}
        <LoadsListDialog
            loads={selectedBar?.loads}
            openDialog={openDialog}
            setOpenDialog={setOpenDialog}
            title={<CustomTitle />}
        />
    </Grid >
}
export default OnTimePerformanceWidget;